import React from 'react';

import { BorderColor as BorderColorIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router';

import { STATUSES_FILTER_OPTIONS } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import { PeoInfoFragment } from 'src/api';
import {
  Table,
  TableColumn,
  TableFilter,
  TableRowAction,
  TableSelectedRowId,
  TableThemes,
  TableTopAction,
} from 'src/components/table';
import { useFilterStorage, useSearch } from 'src/hooks/common';
import { PaginationContextValue } from 'src/providers/PaginationProvider';
import { SelectFieldOption } from 'src/types/components';
import { SortOption } from 'src/types/sort';
import { buildPeoDetailsUrl } from 'src/utils/peo';

type PeosInnerTableProps = {
  peosList: PeoInfoFragment[];
  peosCount: number;
  loading: boolean;
  columns: TableColumn<PeoInfoFragment>[];
  topActions?: Array<TableTopAction>;
  isDetail?: boolean;
  tableTheme?: TableThemes;
  pagination: PaginationContextValue;
  onRefresh?: () => Promise<void>;
  keyFilter?: string;
  searchCategories?: SelectFieldOption[];
};

export const PeosInnerTable: React.FC<PeosInnerTableProps> = ({
  peosList,
  peosCount,
  loading,
  columns,
  topActions,
  isDetail = false,
  tableTheme = TableThemes.default,
  pagination,
  onRefresh,
  keyFilter,
  searchCategories,
}) => {
  const navigate = useNavigate();

  const { setSearchValue, searchValue, setSearchCategory, searchCategory } = useSearch();
  const { setSortOptionByKey } = useFilterStorage();

  const [selectedIds, setSelectedIds] = React.useState<TableSelectedRowId[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue, searchCategory]);

  const handleSort = React.useCallback(
    (sortOption: Nullable<SortOption>) => {
      setSortOptionByKey(keyFilter, sortOption);
    },
    [setSortOptionByKey, keyFilter],
  );

  const rowActions: Array<TableRowAction<PeoInfoFragment>> = React.useMemo(
    () => [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: peo => {
          navigate(buildPeoDetailsUrl(peo.id));
        },
      },
    ],
    [navigate],
  );

  return (
    <Table
      dataSource={peosList}
      rowCount={peosCount}
      columns={columns}
      onSearch={setSearchValue}
      onSearchCategorySelect={setSearchCategory}
      onSort={handleSort}
      rowActions={rowActions}
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      filters={filters}
      topActions={!isDetail ? topActions : []}
      loading={loading}
      pagination={pagination}
      tableTheme={tableTheme}
      onRefresh={onRefresh}
      keyFilter={keyFilter}
      searchCategories={searchCategories}
    />
  );
};

const filters: TableFilter[] = [
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: STATUSES_FILTER_OPTIONS,
    },
  },
];
